import { useCallback, useEffect, useRef } from 'react';

import { Hub } from 'aws-amplify/utils';

import useAuth from 'hooks/authentication/useAuth';
import useSetUserData from 'hooks/authentication/useSetUserData';
import useInitPermission from 'hooks/permission/useInitPermission';

import { logger } from 'utils/logger';
import { ERROR_REASONS } from 'utils/logger/transports/constants';

import { AuthEvent } from 'constants/authEvents';

const AuthInitializer = () => {
    const { getAccessToken } = useAuth();
    const { initPermission } = useInitPermission();
    const { setUserData } = useSetUserData();
    const hasInitializedRef = useRef(false);
    const isInitializingRef = useRef(false);

    const initialize = useCallback(async () => {
        // Prevent concurrent initialization
        if (isInitializingRef.current || hasInitializedRef.current) return;

        isInitializingRef.current = true;

        try {
            // First try to get token normally
            let token = await getAccessToken();

            // If no token, try with force refresh (handles expired tokens)
            token ??= await getAccessToken(true);

            if (!token) {
                hasInitializedRef.current = false;
                isInitializingRef.current = false;
                logger.warn('Auth initialization skipped: no token available after refresh attempt', {
                    tags: {
                        reason: ERROR_REASONS.authInitialization,
                    },
                });
                return;
            }

            // Only initialize if we haven't already
            await Promise.all([initPermission(), setUserData()]);
            hasInitializedRef.current = true;
        } catch (error) {
            hasInitializedRef.current = false;
            logger.error(Error('Auth initialization failed'), {
                cause: error,
                tags: {
                    reason: ERROR_REASONS.authInitialization,
                    errorMsg: error instanceof Error ? error.message : String(error),
                },
            });
        } finally {
            isInitializingRef.current = false;
        }
    }, [getAccessToken, initPermission, setUserData]);

    useEffect(() => {
        void initialize();

        // Listen for auth events
        const unsubscribe = Hub.listen('auth', (data) => {
            const { payload } = data;

            if (payload.event === AuthEvent.SIGNED_IN || payload.event === AuthEvent.TOKEN_REFRESH) {
                hasInitializedRef.current = false;
                void initialize();
            } else if (payload.event === AuthEvent.SIGNED_OUT) {
                hasInitializedRef.current = false;
            }
        });

        return () => {
            unsubscribe();
        };
    }, [initialize]);

    return null; // This component doesn't render anything
};

export default AuthInitializer;
